import {createContentEditableHtmlEditor} from '@frontapp/editor-html';
import {useEditor} from '@frontapp/editor-html/lib/react';
import React, {FC, RefObject, useCallback, useRef, useState} from 'react';
import styled from 'styled-components';

import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {
  popoverWidth,
  PreviewReposition,
} from '../../../../../../libs/shared/ui/src/components/reposition/previewReposition';
import {shadows} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useEditorDarkMode} from '../../../../../../libs/shared/ui/src/styles/darkModeEditorHelpers';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  ellipsisAfterNumberOfLines,
  userHtmlAnchorStyles,
} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {growToFitHeightStyle} from '../../../../../../libs/shared/ui/src/styles/styleHelpers';
import {AnswerModel} from '../../../../../models/api/answerModel';
import {VariableInterpolator} from '../../../../../services/variableInterpolation/variableInterpolator';
import {
  answerVariablePluginDataAttribute,
  createAnswerVariablePlugin,
} from '../../../../common/editor/plugins/text/answerVariablePlugin';
import {AnswerPreviewDropdown} from './answerPreviewDropdown';

/*
 * Constants.
 */

export const answerPreviewPopoverWidth = popoverWidth;

/*
 * Types.
 */

export interface AnswerPreviewProps {
  answer: AnswerModel;
  interpolator: VariableInterpolator;
  onSelect: () => void;
  onDropdownClose?: () => void;
  anchor: RefObject<HTMLElement>;
}

/*
 * Style.
 */

const StyledPreviewDiv = styled.div`
  display: grid;
  grid-template-areas:
    'name menu'
    'answer answer';
  grid-template-columns: 1fr auto;

  overflow: hidden;
  border-radius: 8px;
  background-color: ${(p) => p.theme.foreground};
  ${shadows.dialogsAndDropdowns};

  width: 270px;
`;

const StyledTitleDiv = styled.div`
  grid-area: name;
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  margin: 20px 8px 20px 20px;
  ${ellipsisAfterNumberOfLines(3)}
`;

const StyledIconDiv = styled.div`
  grid-area: menu;
  margin-top: 15px;
  margin-right: 15px;
`;

const StyledAnswerDiv = styled.div`
  grid-area: answer;
  height: 260px;
  overflow-y: scroll;
  margin: 0px 10px 20px;
`;

const StyledEditorDiv = styled.div`
  color: ${(p) => p.theme.composerText};
  padding: 0px 10px;
  line-height: 1.4em;
  ${userHtmlAnchorStyles};
  ${growToFitHeightStyle};

  [${answerVariablePluginDataAttribute}] {
    color: ${(p) => p.theme.greys.white};
    display: inline-block;
    background: ${(p) => p.theme.palette.teal.shade40};
    font-weight: ${fontWeights.semibold};
    border-radius: 10px;
    padding: 1px 7px 3px;
    margin-bottom: 2px;
  }
`;

/*
 * Component.
 */

export const AnswerPreview: FC<AnswerPreviewProps> = (props) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [shouldShowMenu, setShouldShowMenu] = useState(false);
  const {onSelect, onDropdownClose} = props;

  const htmlEditor = useEditor(() => {
    const newEditor = createContentEditableHtmlEditor({
      plugins: [
        createAnswerVariablePlugin({
          getInterpolator: () => props.interpolator,
        }),
      ],
    });

    newEditor.setCleanHtml(props.answer.body);
    return newEditor;
  }, []);

  const editorDarkModeRef = useEditorDarkMode();

  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      htmlEditor.targetRef(node);
      editorDarkModeRef(node);
    },
    [editorDarkModeRef, htmlEditor],
  );

  return (
    <PreviewReposition anchor={props.anchor} popperPlacement="right">
      <StyledPreviewDiv
        onClick={() => {
          onSelect();
          if (onDropdownClose) {
            onDropdownClose();
          }
        }}
      >
        <StyledTitleDiv>{props.answer.name}</StyledTitleDiv>
        <StyledIconDiv>
          <IconButton
            iconName="moreVerticalSmall"
            label={null}
            ref={menuRef}
            onClick={(event) => {
              event.stopPropagation();
              setShouldShowMenu(!shouldShowMenu);
            }}
            size={VisualSizesEnum.SMALL}
          />
        </StyledIconDiv>
        {shouldShowMenu && (
          <AnswerPreviewDropdown
            answer={props.answer}
            anchor={menuRef}
            placement="bottom-end"
            onClose={() => setShouldShowMenu(false)}
          />
        )}
        <StyledAnswerDiv>
          <StyledEditorDiv ref={refCallback} />
        </StyledAnswerDiv>
      </StyledPreviewDiv>
    </PreviewReposition>
  );
};
